"use client"

import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog"
import { Badge } from "../ui/badge"
import { Textarea } from "../ui/textarea"
import { Button } from "../ui/button"
import { CheckCircle, XCircle, Clock, X, Check, Calendar, Timer } from 'lucide-react'
import axios from "axios"
import { apiClient } from "../lib/apiClient"
import { format } from "date-fns"
import { useToast } from "../hooks/use-toast"

interface LeaveReviewModalHRProps {
  isOpen: boolean
  onClose: () => void
  leaveRequest: any // Consider defining a more specific type for leaveRequest
  isViewOnly?: boolean // New prop to control button visibility
  onApprove: (comment: string) => void;
  onReject: (comment: string) => void;
}


export function LeaveReviewModalHR({ isOpen, onClose, leaveRequest, isViewOnly = false, onApprove, onReject }: LeaveReviewModalHRProps) {
  const { toast } = useToast()
  const formatDateTime = (value?: string) => {
    if (!value) return "";
    try {
      const d = new Date(value)
      return `${d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`.replace(/\s/g, "-")
    } catch {
      return value
    }
  }

  const getDayName = (dateString: string | null | undefined) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return format(date, "EEEE");
    } catch {
      return "";
    }
  }

  const getDayTypeColor = (dayType: string) => {
    const lower = dayType?.toLowerCase() || "";
    if (lower === "full day") return "bg-blue-500";
    if (lower === "first-half" || lower === "firsthalf") return "bg-purple-500";
    if (lower === "second-half" || lower === "secondhalf") return "bg-purple-500";
    return "bg-blue-500";
  }

  // Derive manager details from leaveRequest if present
  const managerName = (leaveRequest?.managerName || "--")
  const [resolvedManagerName, setResolvedManagerName] = useState<string>("")

  useEffect(() => {
    const resolveName = async () => {
      try {
        if (!managerName && leaveRequest?.modUser && leaveRequest.modUser !== 0) {
          const res = await apiClient.get("/user/getallusers")
          const users: any[] = res?.data?.users || res?.data || []
          const match = users.find((u: any) => (u.userID ?? u.UserID) === leaveRequest.modUser)
          if (match) {
            setResolvedManagerName(match.userName ?? match.UserName ?? "")
          }
        } else {
          setResolvedManagerName("")
        }
      } catch {
        setResolvedManagerName("")
      }
    }
    if (isOpen) resolveName()
  }, [isOpen, managerName, leaveRequest?.modUser])

  const managerApprovedBy = (managerName && typeof managerName === 'string' && managerName !== '0')
    ? managerName
    : (resolvedManagerName || (leaveRequest?.modUser && leaveRequest.modUser !== 0 ? `Manager (ID: ${leaveRequest.modUser})` : "-"))

  const managerApprovedOn = leaveRequest?.managerResponseOn
    ? formatDateTime(leaveRequest.managerResponseOn)
    : ("--")

  const rawManagerResponse: string = String(leaveRequest?.managerResponse || "").toLowerCase()
  const managerStatus = rawManagerResponse.includes("approved")
    ? "Approved"
    : rawManagerResponse.includes("rejected")
      ? "Rejected"
      : (
        // Fallback inference if explicit managerResponse isn't present
        (leaveRequest?.status === "approved" || leaveRequest?.status === "pending_hr_approval" || leaveRequest?.status === "rejected_by_hr")
          ? "Approved"
          : leaveRequest?.status === "rejected"
            ? "Rejected"
            : "Pending"
      )

  const managerComment = (leaveRequest?.comments || "").trim() || (managerStatus === "Approved" ? "Approved" : managerStatus === "Rejected" ? "Rejected" : "")

  const mockApprovalStatus = {
    manager: {
      status: managerStatus,
      approvedBy: managerApprovedBy,
      approvedOn: managerApprovedOn,
      comment: managerComment,
    },
    hr: {
      status: leaveRequest?.status === "approved"
        ? "Approved"
        : leaveRequest?.status === "rejected_by_hr"
          ? "Rejected"
          : "Pending",
      hrResponse : leaveRequest?.hrResponse || "--",
      approvedBy: leaveRequest?.hrApprovedBy || "--",
      approvedOn: leaveRequest?.hrResponseOn || "--",
      comment: leaveRequest?.hrComment || "--",
    },
  }

  const getApprovalBadge = (status: string) => {
    switch (status) {
      case "Approved":
        return <Badge className="bg-green-100 text-green-700 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Approved</Badge>
      case "Pending":
        return <Badge className="bg-orange-100 text-orange-700 flex items-center gap-1"><Clock className="h-3 w-3" /> Pending</Badge>
      case "Rejected":
        return <Badge className="bg-red-100 text-red-700 flex items-center gap-1"><XCircle className="h-3 w-3" /> Rejected</Badge>
      default:
        return <Badge variant="outline">{status}</Badge>
    }
  }
  const [comment, setComment] = useState<string>("")
  const [submitting, setSubmitting] = useState<boolean>(false)

  useEffect(() => {
    if (isOpen) {
      setComment("")
    }
  }, [isOpen, leaveRequest?.requestID, leaveRequest?.id])

  const callHrAction = async (responseAction: "approved" | "rejected") => {
    if (!leaveRequest?.id && !leaveRequest?.requestID) return
    setSubmitting(true)
    try {
      await axios.post(
        process.env.REACT_APP_API_URL + "/api/Leave/ProcessHRAction",
        { requestID: leaveRequest.id ?? leaveRequest.requestID, response: responseAction === "approved" ? "Approved" : "Rejected", comment: comment || "", modUser: 0 },
        { withCredentials: true }
      )
      if (responseAction === "approved") {
        onApprove(comment)
      } else {
        onReject(comment)
      }
    } catch (e: any) {
      toast({
        title: "Error",
        description: e?.response?.data?.message || e?.message || "Failed to process leave request. Please try again.",
        duration: 4000,
        className: "border-red-500 text-red-800",
      })
      // Don't call callbacks on error
      return
    } finally {
      setSubmitting(false)
      setComment("")
      onClose()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-7xl max-h-[90vh] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
        {/* Fixed Header */}
        <DialogHeader className="border-b border-gray-200 pb-3 px-4 pt-4 flex flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            <DialogTitle className="text-lg font-bold text-gray-900">
              {leaveRequest?.requestBy}
            </DialogTitle>
            <Badge className="text-orange-600 border-orange-200 bg-orange-50 text-xs font-medium">
              {leaveRequest?.leaveType}
            </Badge>
          </div>
          {/* Applied On in header */}
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-600" />
            <div className="flex flex-col items-end">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Applied On</span>
              <span className="text-lg font-semibold text-gray-900">
                {leaveRequest?.appliedOn || "--"}
              </span>
            </div>
          </div>
        </DialogHeader>

        {/* Scrollable Content Area */}
        <div className="space-y-4 px-4">
          {/* Leave Day Details at top with individual day cards */}
          {(() => {
            const dayDetails = leaveRequest?.leaveDayDetails || leaveRequest?.leaveDayDetail || leaveRequest?.leaveDays || [];
            const isValidArray = Array.isArray(dayDetails) && dayDetails.length > 0;
            
            return isValidArray ? (
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="16" y1="2" x2="16" y2="6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="8" y1="2" x2="8" y2="6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="3" y1="10" x2="21" y2="10" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <h3 className="font-semibold text-gray-900 text-base">Leave Day Details</h3>
                </div>
                <div className="flex flex-wrap gap-16">
                  {dayDetails.map((dayDetail: any, index: number) => {
                    const formatDayType = (dayType: string) => {
                      if (!dayType) return "Full Day";
                      const lower = dayType.toLowerCase();
                      if (lower === "first-half" || lower === "firsthalf") return "First-Half";
                      if (lower === "second-half" || lower === "secondhalf") return "Second-Half";
                      return "Full Day";
                    };
                    const formatDateDisplay = (dateString: string | null | undefined) => {
                      if (!dateString) return "--";
                      try {
                        const date = new Date(dateString);
                        if (isNaN(date.getTime())) return String(dateString);
                        return format(date, "dd-MM-yyyy");
                      } catch {
                        return "--";
                      }
                    };
                    const dayTypeColor = getDayTypeColor(dayDetail.dayType);
                    const dayTypeBadge = formatDayType(dayDetail.dayType);
                    const dayName = getDayName(dayDetail.leaveDate);
                    
                    return (
                      <div key={index} className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${dayTypeColor}`}></div>
                        <div className="flex flex-col">
                          <span className="text-base font-semibold text-gray-900">
                            {formatDateDisplay(dayDetail.leaveDate)}
                          </span>
                          <span className="text-xs text-gray-500">{dayName}</span>
                        </div>
                        <Badge className={`${
                          dayTypeBadge === "Full Day" 
                            ? "bg-blue-50 text-blue-700 border-blue-200" 
                            : "bg-purple-50 text-purple-600 border-purple-200"
                        } font-medium text-xs`}>
                          {dayTypeBadge}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null;
          })()}

          {/* Reason for Leave and Duration side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Reason for Leave */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
                <h3 className="font-semibold text-gray-900 text-base">Reason for Leave</h3>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                {leaveRequest?.reason || <span className="italic text-gray-400">No reason provided</span>}
              </p>
            </div>

            {/* Duration */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center">
                  <Timer className="w-4 h-4 text-purple-600" />
                </div>
                <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">DURATION</span>
              </div>
              <p className="text-2xl font-bold text-gray-900 mt-2">
                {leaveRequest?.totalDays || "1"} Day(s)
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {leaveRequest?.fromDate || "-"} - {leaveRequest?.toDate || "-"}
              </p>
            </div>
          </div>

          {/* Approval Status - Manager and HR side by side */}
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900 text-lg">Approval Status</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Manager Approval */}
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-gray-900 text-base">Manager Approval</h4>
                  {getApprovalBadge(mockApprovalStatus.manager.status)}
                </div>
                {mockApprovalStatus.manager.status !== "Pending" && (
                  <div className="space-y-2 text-sm">
                    <div className="flex items-start">
                      <span className="font-medium text-gray-500 w-24">Reviewer</span>
                      <span className="text-gray-900 flex-1">
                        {mockApprovalStatus.manager.approvedBy}
                      </span>
                    </div>
                    <div className="flex items-start">
                      <span className="font-medium text-gray-500 w-24">Reviewed On</span>
                      <span className="text-gray-900 flex-1">
                        {mockApprovalStatus.manager.approvedOn}
                      </span>
                    </div>
                    {mockApprovalStatus.manager.comment && mockApprovalStatus.manager.comment !== '-' && (
                      <div className="pt-2 border-t border-gray-200">
                        <span className="font-medium text-gray-500 block mb-1">Comment</span>
                        <p className="text-gray-700 break-all whitespace-pre-wrap text-sm">
                          {mockApprovalStatus.manager.comment}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* HR Approval */}
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-gray-900 text-base">HR Approval</h4>
                  {getApprovalBadge(mockApprovalStatus.hr.status)}
                </div>
                {mockApprovalStatus.hr.status === "Pending" ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex items-start">
                      <span className="font-medium text-gray-500 w-24">Reviewer</span>
                      <span className="text-gray-900 flex-1">
                        {mockApprovalStatus.hr.approvedBy !== "--" ? mockApprovalStatus.hr.approvedBy : "HR Team"}
                      </span>
                    </div>
                    <div className="text-gray-500 text-sm italic pt-2">
                      Awaiting HR review
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 text-sm">
                    <div className="flex items-start">
                      <span className="font-medium text-gray-500 w-24">Reviewer</span>
                      <span className="text-gray-900 flex-1">
                        {mockApprovalStatus.hr.approvedBy}
                      </span>
                    </div>
                    <div className="flex items-start">
                      <span className="font-medium text-gray-500 w-24">Reviewed On</span>
                      <span className="text-gray-900 flex-1">
                        {mockApprovalStatus.hr.approvedOn}
                      </span>
                    </div>
                    {mockApprovalStatus.hr.comment && mockApprovalStatus.hr.comment !== '--' && (
                      <div className="pt-2 border-t border-gray-200">
                        <span className="font-medium text-gray-500 block mb-1">Comment</span>
                        <p className="text-gray-700 break-all whitespace-pre-wrap text-sm">
                          {mockApprovalStatus.hr.comment}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* HR comment input - show only when not view-only */}
          {!isViewOnly && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Comment (optional)</label>
                <span className="text-xs text-gray-500">
                  {comment.length}/1000
                </span>
              </div>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add a comment for your decision"
                disabled={
                  (leaveRequest?.numericStatus === 3) ||
                  (leaveRequest?.numericStatus === 8) ||
                  (leaveRequest?.numericStatus === 9) ||
                  submitting
                }
                className="min-h-[100px] bg-white border-gray-200 text-gray-900 disabled:opacity-70"
                maxLength={1000}
              />
            </div>
          )}
        </div>

        {/* Fixed Buttons */}
        <div className="border-t border-gray-200 px-4 py-4 flex justify-end gap-3 bg-white">
          {isViewOnly ? (
            <Button onClick={onClose} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 font-medium rounded-md shadow-sm">
              Close
            </Button>
          ) : (
            <>
              <Button 
                variant="destructive" 
                onClick={() => callHrAction("rejected")} 
                disabled={
                  !(mockApprovalStatus.manager.status === "Approved" && mockApprovalStatus.hr.status === "Pending") || submitting
                }
                className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 font-medium rounded-md shadow-sm"
              >
                <X className="h-4 w-4" />
                Reject
              </Button>
              <Button 
                onClick={() => callHrAction("approved")}
                disabled={
                  !(mockApprovalStatus.manager.status === "Approved" && mockApprovalStatus.hr.status === "Pending") || submitting
                } 
                className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 font-medium rounded-md shadow-sm"
              >
                <Check className="h-4 w-4" />
                Approve
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
